import { useIdentity } from '@vp/auth-react';
import { useCallback, useContext, useEffect, useState } from 'react';
import { API_ENDPOINTS } from '../../config/api-config';
import { IApiHooksWithTrigger, IIdentityObject, IUserInfo } from '../../interfaces/app-interfaces';
import { useNRLogging } from '../custom-hooks/useNRLogging';
import { CAContext } from '../../contexts/CAContext';

export interface IUserInfoApiResponse {
  canonicalId: string;
  email: string;
  firstName: string;
  lastName: string;
  isEmailVerified: boolean;
  lastLoginLocale: string;
  canManagePassword: boolean;
  status: string;
}

const useUserInfo = (): IApiHooksWithTrigger<IUserInfo> => {
  const { logCustomAttributesNR } = useNRLogging();
  const {
    setChatState,
    chatState: { enableIsCustomerCheck },
  } = useContext(CAContext);

  const identity = useIdentity();
  const [data, setData] = useState<IUserInfo | undefined>();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>();
  const [toggle, setToggle] = useState(false);
  const trigger = useCallback(() => {
    setToggle(true);
  }, []);
  useEffect(() => {
    const fetchData = async () => {
      if (toggle) {
        const identityObj = identity as IIdentityObject;
        const { authorizationHeader, isSignedIn, profile } = identityObj;
        let isCustomer = true; // default to true
        if (enableIsCustomerCheck) {
          // If config enabled, check is_customer claim from profile
          isCustomer = profile?.['https://claims.cimpress.io/is_customer'] === true || false;
        }
        if (isSignedIn && isCustomer) {
          try {
            setLoading(true);
            logCustomAttributesNR('ca-chatbot-misc-api-request', {
              logLevel: 'info',
              message: 'Fetching user info',
              apiType: 'getUserInfo',
            });
            const res = await fetch(API_ENDPOINTS.USER_PROFILE, {
              headers: {
                Accept: 'application/json',
                Authorization: authorizationHeader,
              },
            });
            if (!res.ok) {
              throw new Error(`Failed to fetch user profile: ${res.status} ${res.statusText}`);
            }
            const responseData = (await res.json()) as IUserInfoApiResponse;
            setData({
              isLoggedIn: true,
              userData: {
                email: responseData.email,
                firstName: responseData.firstName,
                lastName: responseData.lastName,
                shopperId: responseData.canonicalId,
              },
            });

            setChatState((prevState) => ({
              ...prevState,
              userInfo: {
                isLoggedIn: true,
                userData: {
                  email: responseData.email,
                  firstName: responseData.firstName,
                  lastName: responseData.lastName,
                  shopperId: responseData.canonicalId,
                },
              },
            }));
            // setData(res);
          } catch (error) {
            console.error(error);
            logCustomAttributesNR('ca-chatbot-misc-api-error', {
              errorName: error?.name,
              errorStack: error?.stack,
              message: error?.message,
              logLevel: 'error',
              apiType: 'getUserInfo',
            });
            setError(error instanceof Error ? error : new Error('Failed to fetch user info'));
            setData({ isLoggedIn: false });
          } finally {
            setLoading(false);
            setToggle(false);
          }
        } else {
          setData({ isLoggedIn: false });
          setToggle(false);
          setLoading(false);
        }
      }
    };
    fetchData();
  }, [toggle, enableIsCustomerCheck]);
  return { data, loading, error, trigger };
};
export { useUserInfo };
